import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { VisitTimePicker } from "@/components/VisitTimePicker";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";

const timeLabel = (iso, lang) =>
  iso ? new Date(iso).toLocaleTimeString(lang === "hi" ? "hi-IN" : "en-IN", { hour: "2-digit", minute: "2-digit" }) : "--";

const CR_EN = {
  cr_incorrect: "Incorrect time selected", cr_later: "Visit reported later",
  cr_network: "Phone or network problem", cr_forgot: "Forgot to report immediately", cr_other: "Other",
};

export default function Summary() {
  const { session } = useAuth();
  const { lang, t } = useLang();
  const [s, setS] = useState(null);
  const [visits, setVisits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editTime, setEditTime] = useState(null);
  const [reason, setReason] = useState("cr_incorrect");

  const today = new Date().toISOString().slice(0, 10);
  const loadSummary = () => api.get("/summary/today").then(({ data }) => setS(data));
  const loadVisits = () => api.get("/visits", { params: { date: today } }).then(({ data }) => setVisits(data));
  useEffect(() => { loadSummary(); loadVisits(); }, []);

  const submitted = session?.status === "submitted";
  const openEdit = (v) => { setEditing(v); setEditTime(v.visit_time); setReason("cr_incorrect"); };
  const saveTime = async () => {
    try {
      await api.put(`/visits/${editing.id}/time`, { visit_time: editTime, reason: submitted ? CR_EN[reason] : "" });
      toast.success(t("save_time"));
      setEditing(null); loadVisits(); loadSummary();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!s) return <Shell title={t("todays_summary")}><p className="text-slate-400">{t("loading")}</p></Shell>;

  const tiles = [
    { label: t("doctor_visit"), value: s.total_doctor_visits, sub: `${s.doctors_met} met` },
    { label: t("retailer_visit"), value: s.retailer_visits },
    { label: t("stockist_visit"), value: s.stockist_visits },
    { label: t("samples"), value: s.samples },
    { label: t("order_value"), value: money(s.order_value) },
    { label: t("collection"), value: money(s.collection) },
    { label: t("expenses"), value: money(s.expense_total) },
    { label: t("pending_followups"), value: s.pending_followups },
  ];

  return (
    <Shell title={t("todays_summary")}>
      <div className="grid grid-cols-2 gap-3">
        {tiles.map((tl) => (
          <div key={tl.label} className="rounded-xl bg-white border border-slate-200 p-4" data-testid="summary-tile">
            <div className="font-head font-extrabold text-2xl text-slate-800">{tl.value}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{tl.label}</div>
            {tl.sub && <div className="text-[11px] text-emerald-600 font-semibold mt-0.5">{tl.sub}</div>}
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="todays-visits">
        <div className="px-4 py-3 border-b border-slate-100 font-head font-semibold text-slate-800">{t("todays_visits")}</div>
        <div className="divide-y">
          {visits.map((v) => (
            <div key={v.id} className="flex items-center justify-between px-4 py-3" data-testid={`visit-row-${v.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{v.contact_name}</p>
                <p className="text-xs text-slate-500 capitalize">{v.contact_type} · {v.call_status || v.response || v.collection_status || v.payment_status || "-"}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-slate-700" data-testid={`visit-time-${v.id}`}>{timeLabel(v.visit_time, lang)}</span>
                <button onClick={() => openEdit(v)} data-testid={`edit-time-${v.id}`} className="text-slate-400 hover:text-slate-700"><PencilSimple size={18} /></button>
              </div>
            </div>
          ))}
          {visits.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">{t("no_data")}</p>}
        </div>
      </div>

      {Object.keys(s.product_detailing || {}).length > 0 && (
        <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t("products_detailed")}</h3>
          <div className="space-y-2">
            {Object.entries(s.product_detailing).map(([name, cnt]) => (
              <div key={name} className="flex justify-between text-sm"><span className="text-slate-700">{name}</span><span className="font-semibold">{cnt}</span></div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="edit-time-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 space-y-4">
            <h3 className="font-head font-bold text-lg">{t("edit_time")} — {editing.contact_name}</h3>
            <VisitTimePicker value={editTime} onChange={setEditTime} testid="edit-visit-time" />
            {submitted && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("correction_reason")}</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correction-reason" className="mt-1 w-full min-h-[48px] rounded-lg border border-slate-200 px-3 bg-white">
                  {Object.keys(CR_EN).map((k) => <option key={k} value={k}>{t(k)}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} data-testid="edit-time-cancel" className="flex-1 min-h-[48px] rounded-lg border border-slate-300 font-semibold text-slate-600">{t("cancel")}</button>
              <button onClick={saveTime} data-testid="edit-time-save" className="flex-1 min-h-[48px] rounded-lg bg-emerald-600 text-white font-semibold">{t("save_time")}</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
